/**
 * noteFormatCatalog.js (demo-only)
 * ──────────────────────────────────────────────────────────────────────
 * Canonical catalog of the 9 note formats supported by Lumina. Each
 * entry carries a default ordered section list with optional subheadings
 * so the configurator can offer a starting layout that the provider can
 * then rename / reorder / toggle / extend.
 *
 * Section schema:
 *   { id, label, enabled: true, order: <int>, subheadings?: [{id, label}] }
 *
 * NEVER referenced in real mode — gated by useAppMode in callers.
 */

const sect = (id, label, subs = []) => ({
  id, label, enabled: true,
  subheadings: subs.map((s, i) => (typeof s === 'string' ? { id: `${id}-sub-${i}`, label: s } : s)),
});

export const NOTE_FORMATS = [
  {
    id: 'soap',
    label: 'SOAP',
    desc: 'Subjective · Objective · Assessment · Plan. Best for medical / multi-provider teams.',
    sections: [
      sect('subjective', 'Subjective', ['Chief Complaint', 'HPI', 'ROS']),
      sect('objective',  'Objective',  ['Vitals', 'Physical Exam', 'Labs / Imaging']),
      sect('assessment', 'Assessment'),
      sect('plan',       'Plan'),
    ],
  },
  {
    id: 'dap',
    label: 'DAP',
    desc: 'Data · Assessment · Plan. Faster than SOAP — combines S+O. Good for outpatient and private practice.',
    sections: [
      sect('data',       'Data',        ['Subjective', 'Objective']),
      sect('assessment', 'Assessment'),
      sect('plan',       'Plan'),
    ],
  },
  {
    id: 'birp',
    label: 'BIRP',
    desc: 'Behavior · Intervention · Response · Plan. Built for behavioral health and outcome tracking.',
    sections: [
      sect('behavior',     'Behavior'),
      sect('intervention', 'Intervention'),
      sect('response',     'Response'),
      sect('plan',         'Plan'),
    ],
  },
  {
    id: 'girp',
    label: 'GIRP',
    desc: 'Goal · Intervention · Response · Plan. For social work or goal-centered documentation.',
    sections: [
      sect('goal',         'Goal'),
      sect('intervention', 'Intervention'),
      sect('response',     'Response'),
      sect('plan',         'Plan'),
    ],
  },
  {
    id: 'pie',
    label: 'PIE',
    desc: 'Presentation · Intervention · Evaluation. Concise, action-oriented; good for acute settings.',
    sections: [
      sect('presentation', 'Presentation'),
      sect('intervention', 'Intervention'),
      sect('evaluation',   'Evaluation'),
    ],
  },
  {
    id: 'hp',
    label: 'H&P',
    desc: 'History & Physical. Full admission structure for inpatient / consult contexts.',
    sections: [
      sect('chiefComplaint', 'Chief Complaint'),
      sect('hpi',            'History of Present Illness'),
      sect('pastMedHx',      'Past Medical History'),
      sect('medications',    'Medications'),
      sect('allergies',      'Allergies'),
      sect('socialHx',       'Social History'),
      sect('familyHx',       'Family History'),
      sect('ros',            'Review of Systems'),
      sect('vitals',         'Vitals'),
      sect('physical',       'Physical Exam', ['General', 'HEENT', 'Cardiovascular', 'Pulmonary', 'Abdomen', 'Neuro']),
      sect('assessment',     'Assessment'),
      sect('plan',           'Plan'),
    ],
  },
  {
    id: 'consult',
    label: 'Consult Report',
    desc: 'Referral-driven impression + recommendations. Cardiology / specialty consults.',
    sections: [
      sect('chiefComplaint',  'Reason for Consultation'),
      sect('referringSource', 'Referring Source'),
      sect('hpi',             'History of Present Illness'),
      sect('pastMedHx',       'Past Medical History'),
      sect('medications',     'Medications'),
      sect('allergies',       'Allergies'),
      sect('socialHx',        'Social History'),
      sect('familyHx',        'Family History', []),
      sect('ros',             'Review of Systems'),
      sect('vitals',          'Vitals'),
      sect('physical',        'Physical Examination'),
      sect('labsImaging',     'Labs / Imaging Reviewed'),
      sect('assessment',      'Impression'),
      sect('plan',            'Recommendations / Plan'),
    ],
  },
  {
    id: 'narrative',
    label: 'Narrative',
    desc: 'Free-flowing prose, minimal headers. Maximum flexibility, minimal structure.',
    sections: [
      sect('narrative', 'Narrative Note'),
    ],
  },
  {
    id: 'custom',
    label: 'Custom',
    desc: 'Build your own from scratch. Start empty and add only the sections you want.',
    sections: [
      sect('section1', 'Section 1'),
    ],
  },
];

export const noteFormatById = (id) => NOTE_FORMATS.find((f) => f.id === id) || NOTE_FORMATS[0];

export const COPY_FORWARD_RULES = [
  { id: 'always',      label: 'Always copy from last note' },
  { id: 'same-visit',  label: 'Only if same visit type' },
  { id: 'conditional', label: 'Conditional (recency)…' },
  { id: 'never',       label: 'Never copy' },
];

export const DEMO_VISIT_TYPES = [
  { id: 'new-patient',      name: 'New Patient',       duration: 30 },
  { id: 'follow-up',        name: 'Follow-up',         duration: 15 },
  { id: 'sick-visit',       name: 'Sick Visit',        duration: 15 },
  { id: 'wellness',         name: 'Wellness',          duration: 30 },
  { id: 'well-child',       name: 'Well Child',        duration: 30 },
  { id: 'telehealth',       name: 'TeleHealth',        duration: 20 },
  { id: 'phone-encounter',  name: 'Phone Encounter',   duration: 10 },
  { id: 'consult',          name: 'Consult',           duration: 45 },
  { id: 'pre-op',           name: 'Pre-op Evaluation', duration: 30 },
];
